import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { apiGet, apiPost, apiPut } from '@/api/axios-client'
import { getErrorMessage } from '@/api/errors'
import { qk } from '@/api/query-keys'

export type Stage = 'demo_booked' | 'demo_done' | 'pilot_agreed' | 'pilot_running' | 'pilot_review' | 'contract' | 'won' | 'lost'
export type Level = 'green' | 'yellow' | 'red'
export type DealStatus = 'on_track' | 'at_risk' | 'blocked'

export interface PilotFacts {
  successCriteria: string | null
  startAt: string | null
  endAt: string | null
  decisionAt: string | null
  sponsorName: string | null
  sponsorEmail: string | null
  outcome: 'go' | 'no_go' | null
  lostReason: string | null
}

export interface BoardDeal {
  id: string
  name: string
  pipeline: string
  ghlStage: string
  stage: Stage
  inGhl: boolean
  value: number | null
  valuePending: boolean
  owner: { ghlUserId: string | null; name: string | null; email: string | null; memberId: string | null }
  company: { key: string | null; name: string; accountKey: string | null }
  primaryContact: { name: string; email: string | null } | null
  enteredAt: string | null
  daysInStage: number
  level: Level
  reasons: string[]
  stale: boolean
  decisionOverdue: boolean
  nextStep: string | null
  nextStepAt: string | null
  dealStatus: DealStatus | null
  facts: PilotFacts
  factsDone: number
  discoveryDone: number
  discoveryTotal: number
  lastContactAt: string | null
  nextMeeting: { title: string; startAt: string } | null
  lastCheckinAt: string | null
  closeAt: string | null
  project: { id: string; name: string; phase: string; phaseLabel: string; health: Level | null; expectedPhase: string | null } | null
  duplicateOf: string[]
  createdAt: string | null
}

export interface Hygiene {
  counts: { open: number; noNextStep: number; lateNextStep: number; noValue: number; missingFacts: number; stale: number; unmatchedOwner: number }
  line: string
  cleanPct: number
}

export interface Forecast {
  byStage: Array<{ stage: Stage; label: string; count: number; value: number; weighted: number }>
  open: number
  weighted: number
  byMonth: Array<{ month: string; weighted: number }>
}

export interface Board {
  connected: boolean
  stages: Array<{ key: Stage; label: string; phase: string | null }>
  deals: BoardDeal[]
  hygiene: Hygiene
  forecast: Forecast | null
  settings: { ghlWrites: boolean; slack: boolean; autoOpenProject: boolean }
  can: { edit: boolean; seeValue: boolean; openProject: boolean; configure: boolean }
  loadedAt: string
}

export interface HistoryEntry { at: string; by: string; kind: string; text: string; ghl?: 'written' | 'not_written' | 'failed' }

export interface DealDetail {
  deal: BoardDeal
  discovery: Array<{ key: string; label: string; done: boolean }>
  history: HistoryEntry[]
  gates: Array<{ stage: Stage; label: string; missing: string[] }>
  duplicates: Array<{ id: string; name: string; stage: Stage; owner: string | null }>
  clientSuggestions: Array<{ id: string; name: string; why: string }>
  rememberedClientId: string | null
  settings: { ghlWrites: boolean }
  ghlUrl: string
}

export interface Settings {
  enabled: boolean
  ghlWrites: boolean
  slack: boolean
  reportChannelId: string
  checkinDay: number
  checkinHour: number
  reportDay: number
  reportHour: number
  autoOpenProject: boolean
  on: boolean
  production: boolean
}

export interface ReportDeal { id: string; name: string; company: string; owner: string | null; stage: Stage; stageLabel: string; value: number | null; nextStep: string | null; level: Level; reasons: string[]; project: { id: string; name: string } | null }

export interface Report {
  weekOf: string
  hygiene: Hygiene
  forecast: Forecast | null
  counts: Record<string, number>
  won: ReportDeal[]
  lost: Array<ReportDeal & { reason: string | null }>
  newPilots: ReportDeal[]
  pilotsEnding: Array<ReportDeal & { endAt: string | null }>
  decisionsDue: Array<ReportDeal & { decisionAt: string | null }>
  decisionsOverdue: Array<ReportDeal & { decisionAt: string | null }>
  needsAttention: ReportDeal[]
  handoffs: ReportDeal[]
  movedCount: number
}

export interface CheckinList {
  connected: boolean
  deals: BoardDeal[]
  meetings: Array<{ dealId: string; dealName: string; title: string; endedAt: string }>
  weekOf?: string
}

export interface People { connected: boolean; users: Array<{ ghlUserId: string; name: string; email: string | null; member: { id: string; name: string } | null }> }

const enc = encodeURIComponent

export const pipelineApi = {
  settings: () => apiGet<Settings>('pipeline/settings'),
  saveSettings: (body: Partial<Omit<Settings, 'on' | 'production'>>) => apiPut<Settings>('pipeline/settings', body),
  board: (fresh = false) => apiGet<Board>('pipeline/board', fresh ? { fresh: '1' } : undefined),
  deal: (id: string) => apiGet<DealDetail>(`pipeline/deals/${enc(id)}`),
  nextStep: (id: string, nextStep: string | null, nextStepAt: string | null) => apiPut<{ ok: true }>(`pipeline/deals/${enc(id)}/next-step`, { nextStep, nextStepAt }),
  status: (id: string, status: DealStatus | null) => apiPut<{ ok: true }>(`pipeline/deals/${enc(id)}/status`, { status }),
  facts: (id: string, facts: Partial<PilotFacts>) => apiPut<{ ok: true }>(`pipeline/deals/${enc(id)}/facts`, facts),
  discovery: (id: string, key: string, done: boolean) => apiPut<{ ok: true }>(`pipeline/deals/${enc(id)}/discovery`, { key, done }),
  value: (id: string, value: number) => apiPut<{ ok: true }>(`pipeline/deals/${enc(id)}/value`, { value }),
  move: (id: string, stage: Stage, lostReason: string | null) =>
    apiPost<{ moved: boolean; ghl: 'written' | 'not_written' | 'failed'; project: { opened: string } | { needsClient: true } | null }>(`pipeline/deals/${enc(id)}/move`, { stage, lostReason }),
  openProject: (id: string, clientId: string, name: string | null) => apiPost<{ projectId: string; note: string }>(`pipeline/deals/${enc(id)}/open-project`, { clientId, name }),
  checkin: (all: boolean) => apiGet<CheckinList>('pipeline/check-in', all ? { all: '1' } : undefined),
  submitCheckin: (items: Array<{ dealId: string; status: DealStatus; nextStep: string; nextStepAt: string | null; note: string | null }>) =>
    apiPost<{ saved: number; loggedToProjects: number }>('pipeline/check-in', { items }),
  report: () => apiGet<Report>('pipeline/report'),
  test: (kind: 'checkin' | 'report') => apiPost<{ result: string }>('pipeline/test', { kind }),
  people: () => apiGet<People>('pipeline/people'),
  project: (id: string) => apiGet<{ connected: boolean; deals: Array<BoardDeal & { history: HistoryEntry[] }>; off?: boolean }>(`pipeline/projects/${enc(id)}`),
}

/** Whether the pipeline is on for this person. Off, or no access: the menu entries and panels stay hidden. */
export function usePipelineSettings(enabled = true) {
  return useQuery({ queryKey: qk.pipelineSettings(), queryFn: pipelineApi.settings, enabled, staleTime: 5 * 60_000, retry: false })
}

export function usePipelineBoard() {
  return useQuery({ queryKey: qk.pipelineBoard(), queryFn: () => pipelineApi.board(), staleTime: 30_000 })
}

export function usePipelineDeal(id: string | null) {
  return useQuery({ queryKey: qk.pipelineDeal(id ?? ''), queryFn: () => pipelineApi.deal(id!), enabled: Boolean(id), staleTime: 15_000 })
}

/** Every pipeline write: invalidate the whole pipeline, and say what failed in words. */
export function usePipelineWrite<A, R>(fn: (args: A) => Promise<R>, success?: (r: R, args: A) => string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: fn,
    onSuccess: (r, args) => {
      const msg = success?.(r, args)
      if (msg) toast.success(msg)
      void qc.invalidateQueries({ queryKey: qk.pipeline() })
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  })
}
